import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import api from "../../api/api";
import Swal from "sweetalert2";

import {
    Card,
    Row,
    Col,
    Typography,
    Select,
    Button,
    Space,
    Statistic,
    Divider,
    Alert,
    Spin,
    Tag,
    Empty,
    Tooltip,
} from "antd";

import {
    PrinterOutlined,
    BookOutlined,
    ReadOutlined,
    CalendarOutlined,
    FilePdfOutlined,
    SafetyCertificateOutlined,
    CheckCircleOutlined,
    TeamOutlined,
    ReloadOutlined,
    FileSearchOutlined,
    GraduationCap,
    ArrowRightOutlined,
} from "@ant-design/icons";

import { PDFDownloadLink } from "@react-pdf/renderer";

import TestPDF from "./TestPDF";

import { useAcademicYear } from "../../AcademicYearContext";

const { Title, Text, Paragraph } = Typography;

// =====================================================
// PROFESSIONAL SCHOOL BRANDING
// =====================================================

const BRAND = {
    navy: "#062A4B",
    deepBlue: "#063A6B",
    blue: "#0A4C80",
    gold: "#CBA454",
    brightGold: "#F6C944",
    paleGold: "#FFF8E7",
    background: "#F3F6FA",
    white: "#FFFFFF",
    text: "#172B4D",
    muted: "#718096",
    border: "#E2E8F0",
    success: "#16845B",
    danger: "#B42332",
};

// =====================================================
// HELPERS
// =====================================================

const extractList = data => {
    if (Array.isArray(data)) {
        return data;
    }

    if (Array.isArray(data?.results)) {
        return data.results;
    }

    return [];
};

const getRelatedId = value => {
    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return "";
    }

    if (typeof value === "object") {
        return value.id ?? value.pk ?? "";
    }

    return value;
};

const getRelatedName = value => {
    if (!value || typeof value !== "object") {
        return "";
    }

    return (
        value.name ??
        value.title ??
        value.label ??
        ""
    );
};

const getGradeClassId = test =>
    getRelatedId(
        test.grade_class_id ??
        test.grade_class
    );

const getGradeClassName = test => {
    const relatedName = getRelatedName(test.grade_class);

    return (
        test.grade_class_name ??
        relatedName ??
        `Grade Class ${getGradeClassId(test)}`
    );
};

const getSubjectId = test =>
    getRelatedId(
        test.subject_id ??
        test.subject
    );

const getSubjectName = test => {
    const relatedName = getRelatedName(test.subject);

    return (
        test.subject_name ??
        relatedName ??
        `Subject ${getSubjectId(test)}`
    );
};

const getPeriodId = test =>
    getRelatedId(
        test.period_id ??
        test.period
    );

const getPeriodName = test => {
    const relatedName = getRelatedName(test.period);

    return (
        test.period_name ??
        relatedName ??
        `Period ${getPeriodId(test)}`
    );
};

const getPaginationCount = data => {
    if (
        data &&
        !Array.isArray(data) &&
        data.count !== null &&
        data.count !== undefined &&
        data.count !== ""
    ) {
        const count = Number(data.count);

        if (
            Number.isFinite(count) &&
            count >= 0
        ) {
            return count;
        }
    }

    return null;
};

const getErrorMessage = error =>
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.message ||
    "An unexpected error occurred. Please try again.";

const selectStyles = {
    width: "100%",
    marginTop: 9,
};

const selectDropdownStyle = {
    borderRadius: 10,
};

// =====================================================
// REUSABLE STATISTIC CARD
// =====================================================

const OverviewCard = ({
    title,
    value,
    icon,
    description,
    loading,
    accent = BRAND.navy,
}) => (
    <Card
        bordered={false}
        className="ptbc-overview-card"
        style={{
            height: "100%",
            borderRadius: 17,
            background: BRAND.white,
            border: `1px solid ${BRAND.border}`,
            boxShadow: "0 5px 18px rgba(6,42,75,0.045)",
            overflow: "hidden",
            position: "relative",
        }}
        styles={{
            body: {
                padding: "22px 21px",
            },
        }}
    >
        <div
            style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: 4,
                height: "100%",
                background: accent,
            }}
        />

        <div
            style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 12,
            }}
        >
            <div style={{ minWidth: 0, flex: 1 }}>
                <Text
                    style={{
                        display: "block",
                        color: BRAND.muted,
                        fontSize: 12,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: 0.8,
                        marginBottom: 10,
                    }}
                >
                    {title}
                </Text>

                <Statistic
                    value={value}
                    loading={loading}
                    valueStyle={{
                        color: BRAND.navy,
                        fontSize: 31,
                        lineHeight: 1.15,
                        fontWeight: 800,
                    }}
                />

                <Text
                    style={{
                        display: "block",
                        marginTop: 9,
                        color: BRAND.muted,
                        fontSize: 12,
                    }}
                >
                    {description}
                </Text>
            </div>

            <div
                style={{
                    width: 47,
                    height: 47,
                    minWidth: 47,
                    borderRadius: 14,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background:
                        accent === BRAND.gold
                            ? BRAND.paleGold
                            : "#EAF1F8",
                    color: accent,
                    fontSize: 21,
                    border: `1px solid ${
                        accent === BRAND.gold
                            ? "#F1E1B8"
                            : "#DCE7F2"
                    }`,
                }}
            >
                {icon}
            </div>
        </div>
    </Card>
);

// =====================================================
// SELECTION FIELD
// =====================================================

const SelectionField = ({
    number,
    title,
    description,
    children,
}) => (
    <div
        style={{
            height: "100%",
            padding: 18,
            background: "#FBFCFE",
            border: `1px solid ${BRAND.border}`,
            borderRadius: 15,
        }}
    >
        <div
            style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
                marginBottom: 7,
            }}
        >
            <div
                style={{
                    width: 34,
                    height: 34,
                    minWidth: 34,
                    borderRadius: 11,
                    background: BRAND.navy,
                    color: BRAND.brightGold,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    fontWeight: 800,
                    fontSize: 14,
                    boxShadow: "0 3px 8px rgba(6,42,75,0.14)",
                }}
            >
                {number}
            </div>

            <div>
                <Text
                    strong
                    style={{
                        display: "block",
                        color: BRAND.navy,
                        fontSize: 14,
                    }}
                >
                    {title}
                </Text>

                <Text
                    type="secondary"
                    style={{
                        fontSize: 12,
                    }}
                >
                    {description}
                </Text>
            </div>
        </div>

        {children}
    </div>
);

// =====================================================
// COMPONENT
// =====================================================

const PrintTestByClass = () => {
    const [tests, setTests] = useState([]);
    const [serverTestCount, setServerTestCount] = useState(0);
    const [loadingTests, setLoadingTests] = useState(true);

    const [selectedGradeClass, setSelectedGradeClass] = useState("");
    const [selectedSubject, setSelectedSubject] = useState("");
    const [selectedPeriod, setSelectedPeriod] = useState("");

    const [printing, setPrinting] = useState(false);
    const [preparedTest, setPreparedTest] = useState(null);

    const {
        selectedYear,
        loading: loadingAcademicYear,
    } = useAcademicYear();

    const token = localStorage.getItem("access");

    const authHeaders = useMemo(
        () => ({
            Authorization: `Bearer ${token}`,
        }),
        [token]
    );

    // =====================================================
    // FETCH ALL TEST PAGES
    // =====================================================

    const fetchAllTests = useCallback(
        async (academicYearId, signal) => {
            const collectedTests = [];
            const seenIds = new Set();

            let page = 1;
            let expectedCount = null;
            let nextUrl = null;
            let useNextUrl = false;

            const MAX_PAGES = 10000;

            while (page <= MAX_PAGES) {
                if (signal?.aborted) {
                    throw new Error("REQUEST_CANCELLED");
                }

                const response = await api.get(
                    useNextUrl && nextUrl
                        ? nextUrl
                        : "/api/periodic-tests/",
                    {
                        headers: authHeaders,
                        ...(!useNextUrl && {
                            params: {
                                academic_year: academicYearId,
                                page,
                                page_size: 100,
                            },
                        }),
                        signal,
                    }
                );

                if (signal?.aborted) {
                    throw new Error("REQUEST_CANCELLED");
                }

                const data = response.data;
                const pageResults = extractList(data);

                if (Array.isArray(data)) {
                    for (const test of pageResults) {
                        const key =
                            test?.id !== undefined &&
                            test?.id !== null
                                ? String(test.id)
                                : null;

                        if (key !== null) {
                            if (seenIds.has(key)) continue;
                            seenIds.add(key);
                        }

                        collectedTests.push(test);
                    }

                    expectedCount = collectedTests.length;
                    break;
                }

                if (page === 1) {
                    expectedCount = getPaginationCount(data);
                }

                for (const test of pageResults) {
                    const key =
                        test?.id !== undefined &&
                        test?.id !== null
                            ? String(test.id)
                            : null;

                    if (key !== null) {
                        if (seenIds.has(key)) continue;
                        seenIds.add(key);
                    }

                    collectedTests.push(test);
                }

                if (data?.next) {
                    nextUrl = data.next;
                    useNextUrl = true;
                    page += 1;
                    continue;
                }

                if (
                    expectedCount !== null &&
                    collectedTests.length < expectedCount &&
                    pageResults.length > 0
                ) {
                    page += 1;
                    useNextUrl = false;
                    nextUrl = null;
                    continue;
                }

                break;
            }

            if (page > MAX_PAGES) {
                throw new Error(
                    "Test pagination exceeded the safety limit."
                );
            }

            return {
                tests: collectedTests,
                serverCount:
                    expectedCount !== null
                        ? expectedCount
                        : collectedTests.length,
            };
        },
        [authHeaders]
    );

    // =====================================================
    // LOAD TESTS FOR THE SELECTED ACADEMIC YEAR
    // =====================================================

    const loadTests = useCallback(async signal => {
        if (loadingAcademicYear) {
            return;
        }

        if (!selectedYear?.id) {
            setTests([]);
            setServerTestCount(0);
            setLoadingTests(false);
            setSelectedGradeClass("");
            setSelectedSubject("");
            setSelectedPeriod("");
            setPreparedTest(null);
            return;
        }

        setLoadingTests(true);
        setTests([]);
        setServerTestCount(0);
        setSelectedGradeClass("");
        setSelectedSubject("");
        setSelectedPeriod("");
        setPreparedTest(null);

        try {
            const result = await fetchAllTests(
                selectedYear.id,
                signal
            );

            if (signal?.aborted) return;

            setTests(result.tests);
            setServerTestCount(result.serverCount);

            if (result.serverCount !== result.tests.length) {
                console.warn(
                    "Test count verification:",
                    {
                        serverCount: result.serverCount,
                        retrievedCount: result.tests.length,
                    }
                );
            }
        } catch (error) {
            if (
                signal?.aborted ||
                error?.message === "REQUEST_CANCELLED" ||
                error?.code === "ERR_CANCELED"
            ) {
                return;
            }

            console.error(
                "Failed to load periodic tests:",
                error?.response?.data || error
            );

            setTests([]);
            setServerTestCount(0);

            Swal.fire({
                icon: "error",
                title: "Unable to Load Tests",
                text: getErrorMessage(error),
                confirmButtonColor: BRAND.navy,
            });
        } finally {
            if (!signal?.aborted) {
                setLoadingTests(false);
            }
        }
    }, [
        loadingAcademicYear,
        selectedYear?.id,
        fetchAllTests,
    ]);

    useEffect(() => {
        const controller = new AbortController();

        loadTests(controller.signal);

        return () => controller.abort();
    }, [loadTests]);

    // =====================================================
    // GRADE CLASS OPTIONS
    // =====================================================

    const gradeClassOptions = useMemo(() => {
        const map = new Map();

        for (const test of tests) {
            const id = getGradeClassId(test);

            if (
                id === "" ||
                id === null ||
                id === undefined
            ) {
                continue;
            }

            const key = String(id);

            if (!map.has(key)) {
                map.set(key, {
                    id,
                    name: getGradeClassName(test),
                });
            }
        }

        return [...map.values()].sort((a, b) =>
            String(a.name).localeCompare(String(b.name))
        );
    }, [tests]);

    // =====================================================
    // SUBJECT OPTIONS
    // =====================================================

    const subjectOptions = useMemo(() => {
        if (!selectedGradeClass) return [];

        const map = new Map();

        for (const test of tests) {
            if (
                String(getGradeClassId(test)) !==
                String(selectedGradeClass)
            ) {
                continue;
            }

            const id = getSubjectId(test);

            if (
                id === "" ||
                id === null ||
                id === undefined
            ) {
                continue;
            }

            const key = String(id);

            if (!map.has(key)) {
                map.set(key, {
                    id,
                    name: getSubjectName(test),
                });
            }
        }

        return [...map.values()].sort((a, b) =>
            String(a.name).localeCompare(String(b.name))
        );
    }, [tests, selectedGradeClass]);

    // =====================================================
    // PERIOD OPTIONS
    // =====================================================

    const periodOptions = useMemo(() => {
        if (!selectedGradeClass || !selectedSubject) {
            return [];
        }

        const map = new Map();

        for (const test of tests) {
            if (
                String(getGradeClassId(test)) !==
                    String(selectedGradeClass) ||
                String(getSubjectId(test)) !==
                    String(selectedSubject)
            ) {
                continue;
            }

            const id = getPeriodId(test);

            if (
                id === "" ||
                id === null ||
                id === undefined
            ) {
                continue;
            }

            const key = String(id);

            if (!map.has(key)) {
                map.set(key, {
                    id,
                    name: getPeriodName(test),
                });
            }
        }

        return [...map.values()].sort((a, b) =>
            String(a.name).localeCompare(String(b.name))
        );
    }, [tests, selectedGradeClass, selectedSubject]);

    // =====================================================
    // FIND SELECTED TEST
    // =====================================================

    const findMatchingTestSummary = useCallback(
        () =>
            tests.find(test =>
                String(getGradeClassId(test)) ===
                    String(selectedGradeClass) &&
                String(getSubjectId(test)) ===
                    String(selectedSubject) &&
                String(getPeriodId(test)) ===
                    String(selectedPeriod)
            ),
        [
            tests,
            selectedGradeClass,
            selectedSubject,
            selectedPeriod,
        ]
    );

    // =====================================================
    // FETCH FULL TEST DETAIL
    // =====================================================

    const fetchTestDetail = async testId => {
        const response = await api.get(
            `/api/periodic-tests/${testId}/`,
            { headers: authHeaders }
        );

        return response.data;
    };

    // =====================================================
    // FETCH ALL ENROLLED STUDENTS
    // =====================================================

    const fetchEnrolledStudents = async gradeClassId => {
        if (!selectedYear?.id) return [];

        const collectedStudents = [];
        const seenIds = new Set();

        let page = 1;
        let nextUrl = null;
        let useNextUrl = false;
        let expectedCount = null;

        const MAX_PAGES = 10000;

        while (page <= MAX_PAGES) {
            const response = await api.get(
                useNextUrl && nextUrl
                    ? nextUrl
                    : "/api/users/students/",
                {
                    headers: authHeaders,
                    ...(!useNextUrl && {
                        params: {
                            grade_class: gradeClassId,
                            status: "Enrolled",
                            academic_year: selectedYear.id,
                            page,
                            page_size: 100,
                        },
                    }),
                }
            );

            const data = response.data;
            const results = extractList(data);

            if (page === 1) {
                expectedCount = getPaginationCount(data);
            }

            for (const student of results) {
                const key =
                    student?.id !== undefined &&
                    student?.id !== null
                        ? String(student.id)
                        : null;

                if (key !== null) {
                    if (seenIds.has(key)) continue;
                    seenIds.add(key);
                }

                collectedStudents.push(student);
            }

            if (Array.isArray(data)) break;

            if (data?.next) {
                nextUrl = data.next;
                useNextUrl = true;
                page += 1;
                continue;
            }

            if (
                expectedCount !== null &&
                collectedStudents.length < expectedCount &&
                results.length > 0
            ) {
                page += 1;
                nextUrl = null;
                useNextUrl = false;
                continue;
            }

            break;
        }

        if (page > MAX_PAGES) {
            throw new Error(
                "Student pagination exceeded the safety limit."
            );
        }

        return collectedStudents;
    };

    // =====================================================
    // PREPARE STUDENT TEST PAPERS
    // =====================================================

    const prepareStudentTests = (test, students) =>
        students.map(student => {
            const studentName =
                student.full_name ||
                student.user?.full_name ||
                [
                    student.first_name,
                    student.last_name,
                ]
                    .filter(Boolean)
                    .join(" ") ||
                student.username ||
                "Student";

            const admissionNumber =
                student.admission_number ??
                student.student_admission_number ??
                student.admissions?.[0]?.admission_number ??
                "";

            return {
                ...test,
                student_id: student.id,
                student_name: studentName,
                full_name: studentName,
                student_admission: admissionNumber,
                student_admission_number: admissionNumber,
            };
        });

    // =====================================================
    // PREPARE TEST PDF DATA
    // =====================================================

    const generateTestPDF = async () => {
        if (
            !selectedGradeClass ||
            !selectedSubject ||
            !selectedPeriod
        ) {
            await Swal.fire({
                icon: "warning",
                title: "Complete Your Selection",
                text:
                    "Please select a Grade Class, Subject, and Period.",
                confirmButtonColor: BRAND.navy,
            });

            return;
        }

        const summary = findMatchingTestSummary();

        if (!summary) {
            await Swal.fire({
                icon: "info",
                title: "No Matching Test",
                text:
                    "No test was found for this Grade Class, Subject, and Period.",
                confirmButtonColor: BRAND.navy,
            });

            return;
        }

        setPrinting(true);
        setPreparedTest(null);

        try {
            const test = await fetchTestDetail(summary.id);

            const students = await fetchEnrolledStudents(
                selectedGradeClass
            );

            if (!students.length) {
                await Swal.fire({
                    icon: "info",
                    title: "No Enrolled Students",
                    text:
                        "No enrolled students were found in this Grade Class for the selected academic year.",
                    confirmButtonColor: BRAND.navy,
                });

                return;
            }

            const studentTests = prepareStudentTests(
                test,
                students
            );

            setPreparedTest({
                test,
                students,
                studentTests,
            });

            await Swal.fire({
                icon: "success",
                title: "Test Papers Prepared",
                text:
                    `${students.length} student test paper(s) are ready for download.`,
                confirmButtonColor: BRAND.navy,
                timer: 2200,
                showConfirmButton: false,
            });
        } catch (error) {
            console.error(
                "Failed to prepare test PDF:",
                error?.response?.data || error
            );

            setPreparedTest(null);

            await Swal.fire({
                icon: "error",
                title: "Preparation Failed",
                text: getErrorMessage(error),
                confirmButtonColor: BRAND.navy,
            });
        } finally {
            setPrinting(false);
        }
    };

    // =====================================================
    // RESET CURRENT SELECTION
    // =====================================================

    const resetSelection = () => {
        setSelectedGradeClass("");
        setSelectedSubject("");
        setSelectedPeriod("");
        setPreparedTest(null);
    };

    // =====================================================
    // FILE NAME
    // =====================================================

    const pdfFileName =
        `test_${selectedSubject || "subject"}_` +
        `${selectedGradeClass || "class"}_` +
        `${selectedPeriod || "period"}_students.pdf`;

    const busy =
        loadingTests ||
        loadingAcademicYear ||
        printing;

    const disabled =
        busy ||
        !selectedGradeClass ||
        !selectedSubject ||
        !selectedPeriod;

    const selectedGradeClassName =
        gradeClassOptions.find(
            item =>
                String(item.id) ===
                String(selectedGradeClass)
        )?.name || "";

    const selectedSubjectName =
        subjectOptions.find(
            item =>
                String(item.id) ===
                String(selectedSubject)
        )?.name || "";

    const selectedPeriodName =
        periodOptions.find(
            item =>
                String(item.id) ===
                String(selectedPeriod)
        )?.name || "";

    const isReady = Boolean(preparedTest);

    // =====================================================
    // UI
    // =====================================================

    return (
        <div className="ptbc-page">
            <style>{`
                .ptbc-page {
                    min-height: 100vh;
                    padding: 26px;
                    color: ${BRAND.text};
                    background: ${BRAND.background};
                    box-sizing: border-box;
                }

                .ptbc-page *,
                .ptbc-page *::before,
                .ptbc-page *::after {
                    box-sizing: border-box;
                }

                .ptbc-hero {
                    position: relative;
                    overflow: hidden;
                    border-radius: 21px;
                    padding: 30px 32px;
                    background: linear-gradient(
                        118deg,
                        ${BRAND.navy} 0%,
                        ${BRAND.deepBlue} 67%,
                        #0A527D 100%
                    );
                    border: 1px solid rgba(246,201,68,0.30);
                    box-shadow: 0 12px 30px rgba(6,42,75,0.15);
                }

                .ptbc-hero::before {
                    content: "";
                    position: absolute;
                    width: 245px;
                    height: 245px;
                    border-radius: 50%;
                    right: 6%;
                    top: -145px;
                    border: 35px solid rgba(246,201,68,0.075);
                    pointer-events: none;
                }

                .ptbc-hero::after {
                    content: "";
                    position: absolute;
                    width: 175px;
                    height: 175px;
                    border-radius: 50%;
                    right: -28px;
                    bottom: -115px;
                    background: rgba(255,255,255,0.045);
                    border: 1px solid rgba(255,255,255,0.08);
                    pointer-events: none;
                }

                .ptbc-hero-content {
                    position: relative;
                    z-index: 1;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 22px;
                }

                .ptbc-hero-icon {
                    width: 58px;
                    height: 58px;
                    min-width: 58px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    border-radius: 17px;
                    background: rgba(246,201,68,0.12);
                    color: ${BRAND.brightGold};
                    border: 1px solid rgba(246,201,68,0.38);
                    font-size: 27px;
                }

                .ptbc-hero-title {
                    color: #FFFFFF !important;
                    margin: 0 !important;
                    font-size: clamp(22px, 2.2vw, 30px) !important;
                    font-weight: 800 !important;
                    letter-spacing: -0.55px;
                    line-height: 1.3 !important;
                }

                .ptbc-hero-subtitle {
                    color: rgba(255,255,255,0.76) !important;
                    font-size: 13px;
                    line-height: 1.8;
                    margin-top: 7px;
                    max-width: 650px;
                }

                .ptbc-year-panel {
                    position: relative;
                    z-index: 1;
                    flex-shrink: 0;
                    min-width: 175px;
                    padding: 13px 16px;
                    border-radius: 13px;
                    border: 1px solid rgba(246,201,68,0.35);
                    background: rgba(0,0,0,0.12);
                }

                .ptbc-year-label {
                    display: block;
                    color: rgba(255,255,255,0.65);
                    font-size: 10px;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    margin-bottom: 5px;
                }

                .ptbc-year-value {
                    color: ${BRAND.brightGold};
                    font-size: 14px;
                    font-weight: 800;
                    overflow-wrap: anywhere;
                }

                .ptbc-section-card {
                    border: 1px solid ${BRAND.border} !important;
                    border-radius: 18px !important;
                    box-shadow: 0 5px 20px rgba(6,42,75,0.035) !important;
                    overflow: hidden;
                }

                .ptbc-section-heading {
                    color: ${BRAND.navy};
                    font-size: 17px;
                    font-weight: 800;
                    letter-spacing: -0.2px;
                }

                .ptbc-section-caption {
                    color: ${BRAND.muted};
                    font-size: 12px;
                    line-height: 1.7;
                }

                .ptbc-field-label {
                    color: ${BRAND.navy};
                    font-size: 13px;
                    font-weight: 700;
                }

                .ptbc-page .ant-select-selector {
                    min-height: 45px !important;
                    display: flex;
                    align-items: center;
                    border-radius: 10px !important;
                    border-color: #D8E1EB !important;
                    box-shadow: none !important;
                }

                .ptbc-page .ant-select-focused .ant-select-selector,
                .ptbc-page .ant-select-selector:hover {
                    border-color: ${BRAND.gold} !important;
                }

                .ptbc-page .ant-select-selection-item,
                .ptbc-page .ant-select-selection-placeholder {
                    line-height: 43px !important;
                }

                .ptbc-primary-btn {
                    min-height: 48px;
                    padding: 0 25px;
                    border-radius: 11px;
                    background: ${BRAND.navy} !important;
                    border: 1px solid ${BRAND.gold} !important;
                    color: #FFFFFF !important;
                    font-weight: 750;
                    box-shadow: 0 6px 15px rgba(6,42,75,0.15);
                    transition: all 0.2s ease;
                }

                .ptbc-primary-btn:hover:not(:disabled) {
                    background: ${BRAND.deepBlue} !important;
                    border-color: ${BRAND.brightGold} !important;
                    transform: translateY(-1px);
                    box-shadow: 0 9px 20px rgba(6,42,75,0.2);
                }

                .ptbc-download-btn {
                    min-height: 48px;
                    padding: 0 25px;
                    border-radius: 11px;
                    color: ${BRAND.navy} !important;
                    background: ${BRAND.brightGold} !important;
                    border: 1px solid ${BRAND.gold} !important;
                    font-weight: 800;
                    box-shadow: 0 6px 15px rgba(203,164,84,0.20);
                    transition: all 0.2s ease;
                }

                .ptbc-download-btn:hover:not(:disabled) {
                    color: ${BRAND.navy} !important;
                    background: #FFDA68 !important;
                    border-color: ${BRAND.gold} !important;
                    transform: translateY(-1px);
                }

                .ptbc-secondary-btn {
                    min-height: 42px;
                    border-radius: 10px;
                    color: ${BRAND.navy} !important;
                    border-color: #D8E1EB !important;
                    font-weight: 650;
                }

                .ptbc-ready-panel {
                    border-radius: 16px;
                    padding: 19px;
                    background: linear-gradient(
                        135deg,
                        #F0F7F4 0%,
                        #FFFFFF 100%
                    );
                    border: 1px solid #CDE7D9;
                }

                .ptbc-ready-number {
                    color: ${BRAND.success};
                    font-size: 27px;
                    font-weight: 850;
                    line-height: 1.2;
                }

                .ptbc-step-pill {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 6px 10px;
                    border-radius: 8px;
                    background: #EDF3F9;
                    color: ${BRAND.navy};
                    font-size: 11px;
                    font-weight: 750;
                }

                .ptbc-page .ant-alert {
                    border-radius: 12px;
                }

                .ptbc-page .ant-divider-horizontal.ant-divider-with-text {
                    margin: 20px 0;
                }

                .ptbc-page .ant-divider-inner-text {
                    color: ${BRAND.navy};
                    font-weight: 750;
                    font-size: 12px;
                }

                .ptbc-page .ant-card-body {
                    min-width: 0;
                }

                @media (max-width: 767px) {
                    .ptbc-page {
                        padding: 13px;
                    }

                    .ptbc-hero {
                        padding: 22px 19px;
                        border-radius: 16px;
                    }

                    .ptbc-hero-content {
                        align-items: flex-start;
                        flex-direction: column;
                    }

                    .ptbc-hero-icon {
                        width: 48px;
                        height: 48px;
                        min-width: 48px;
                        font-size: 22px;
                        border-radius: 14px;
                    }

                    .ptbc-year-panel {
                        width: 100%;
                    }

                    .ptbc-primary-btn,
                    .ptbc-download-btn {
                        width: 100%;
                    }
                }

                @media (prefers-reduced-motion: reduce) {
                    .ptbc-primary-btn,
                    .ptbc-download-btn {
                        transition: none;
                    }
                }
            `}</style>

            {/* =========================================
                HERO HEADER
            ========================================= */}

            <div className="ptbc-hero">
                <div className="ptbc-hero-content">
                    <div
                        style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 17,
                            minWidth: 0,
                        }}
                    >
                        <div className="ptbc-hero-icon">
                            <PrinterOutlined />
                        </div>

                        <div style={{ minWidth: 0 }}>
                            <Text
                                style={{
                                    display: "block",
                                    color: BRAND.brightGold,
                                    fontSize: 10,
                                    fontWeight: 800,
                                    letterSpacing: 1.8,
                                    textTransform: "uppercase",
                                    marginBottom: 8,
                                }}
                            >
                                EXAMINATION MANAGEMENT
                            </Text>

                            <Title className="ptbc-hero-title">
                                Print Test By Class
                            </Title>

                            <Paragraph className="ptbc-hero-subtitle">
                                Prepare and download organized
                                examination papers for every enrolled
                                student in a selected grade class.
                                Choose a class, course, and period to
                                get started.
                            </Paragraph>

                            <Space size={[8, 8]} wrap>
                                <Tag
                                    icon={<SafetyCertificateOutlined />}
                                    style={{
                                        color: "#FFFFFF",
                                        background: "rgba(255,255,255,0.09)",
                                        borderColor: "rgba(255,255,255,0.18)",
                                        borderRadius: 7,
                                        padding: "4px 9px",
                                        marginInlineEnd: 0,
                                    }}
                                >
                                    Academic Records
                                </Tag>

                                <Tag
                                    icon={<FilePdfOutlined />}
                                    style={{
                                        color: BRAND.brightGold,
                                        background: "rgba(246,201,68,0.10)",
                                        borderColor: "rgba(246,201,68,0.25)",
                                        borderRadius: 7,
                                        padding: "4px 9px",
                                        marginInlineEnd: 0,
                                    }}
                                >
                                    PDF Generation
                                </Tag>
                            </Space>
                        </div>
                    </div>

                    <div className="ptbc-year-panel">
                        <span className="ptbc-year-label">
                            <CalendarOutlined /> Academic Year
                        </span>

                        <div className="ptbc-year-value">
                            {loadingAcademicYear
                                ? "Loading academic year..."
                                : selectedYear?.name ||
                                  "No Active Academic Year"}
                        </div>

                        <div
                            style={{
                                marginTop: 9,
                                color: "rgba(255,255,255,0.65)",
                                fontSize: 11,
                            }}
                        >
                            {loadingAcademicYear
                                ? "Please wait"
                                : selectedYear?.id
                                    ? "Current selection"
                                    : "Select an academic year first"}
                        </div>
                    </div>
                </div>
            </div>

            {/* =========================================
                STATISTICS
            ========================================= */}

            <Row
                gutter={[16, 16]}
                style={{
                    marginTop: 22,
                    marginBottom: 22,
                }}
            >
                <Col xs={24} sm={12} lg={8}>
                    <OverviewCard
                        title="Available Tests"
                        value={serverTestCount}
                        icon={<BookOutlined />}
                        description="Tests recorded for this academic year"
                        loading={loadingTests || loadingAcademicYear}
                        accent={BRAND.navy}
                    />
                </Col>

                <Col xs={24} sm={12} lg={8}>
                    <OverviewCard
                        title="Grade Classes"
                        value={gradeClassOptions.length}
                        icon={<ReadOutlined />}
                        description="Classes with available tests"
                        loading={loadingTests}
                        accent={BRAND.gold}
                    />
                </Col>

                <Col xs={24} sm={12} lg={8}>
                    <OverviewCard
                        title="Matching Periods"
                        value={periodOptions.length}
                        icon={<CalendarOutlined />}
                        description={
                            selectedGradeClass && selectedSubject
                                ? "Periods available for your selection"
                                : "Select a class and course to view periods"
                        }
                        loading={loadingTests}
                        accent={BRAND.blue}
                    />
                </Col>
            </Row>

            {/* =========================================
                MAIN SELECTION PANEL
            ========================================= */}

            <Card
                className="ptbc-section-card"
                bordered={false}
                styles={{
                    body: {
                        padding: 0,
                    },
                }}
            >
                <div
                    style={{
                        padding: "23px 25px",
                        borderBottom: `1px solid ${BRAND.border}`,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 15,
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 13,
                        }}
                    >
                        <div
                            style={{
                                width: 43,
                                height: 43,
                                borderRadius: 13,
                                background: BRAND.paleGold,
                                color: BRAND.navy,
                                border: "1px solid #F0E1B8",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 20,
                            }}
                        >
                            <FileSearchOutlined />
                        </div>

                        <div>
                            <div className="ptbc-section-heading">
                                Examination Selection
                            </div>

                            <div className="ptbc-section-caption">
                                Select the examination details before
                                preparing the student papers.
                            </div>
                        </div>
                    </div>

                    <Space wrap>
                        <Tag
                            color={
                                loadingTests
                                    ? "processing"
                                    : serverTestCount > 0
                                        ? "success"
                                        : "default"
                            }
                            style={{
                                borderRadius: 7,
                                padding: "4px 9px",
                                marginInlineEnd: 0,
                            }}
                        >
                            {loadingTests
                                ? "Loading tests"
                                : `${tests.length} tests loaded`}
                        </Tag>

                        <Tooltip title="Clear the current class, course, and period selection">
                            <Button
                                className="ptbc-secondary-btn"
                                icon={<ReloadOutlined />}
                                onClick={resetSelection}
                                disabled={
                                    !selectedGradeClass &&
                                    !selectedSubject &&
                                    !selectedPeriod &&
                                    !preparedTest
                                }
                            >
                                Reset
                            </Button>
                        </Tooltip>
                    </Space>
                </div>

                <div style={{ padding: 25 }}>
                    {loadingAcademicYear || loadingTests ? (
                        <div
                            style={{
                                minHeight: 220,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 14,
                            }}
                        >
                            <Spin
                                size="large"
                                indicator={
                                    <Spin
                                        size="large"
                                        style={{
                                            color: BRAND.navy,
                                        }}
                                    />
                                }
                            />

                            <Text
                                strong
                                style={{
                                    color: BRAND.navy,
                                }}
                            >
                                Loading examination records
                            </Text>

                            <Text
                                type="secondary"
                                style={{
                                    fontSize: 12,
                                    textAlign: "center",
                                }}
                            >
                                Retrieving available tests for the
                                selected academic year...
                            </Text>
                        </div>
                    ) : !selectedYear?.id ? (
                        <Alert
                            showIcon
                            type="warning"
                            message="Academic Year Required"
                            description="There is no selected academic year. Please select an academic year in your school system before preparing examination papers."
                        />
                    ) : serverTestCount === 0 ? (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                                <div>
                                    <Text
                                        strong
                                        style={{
                                            display: "block",
                                            color: BRAND.navy,
                                        }}
                                    >
                                        No Tests Available
                                    </Text>

                                    <Text
                                        type="secondary"
                                        style={{
                                            fontSize: 12,
                                        }}
                                    >
                                        No examination tests were found
                                        for {selectedYear?.name}.
                                    </Text>
                                </div>
                            }
                        >
                            <Button
                                className="ptbc-secondary-btn"
                                icon={<ReloadOutlined />}
                                onClick={() => {
                                    const controller =
                                        new AbortController();

                                    loadTests(controller.signal);
                                }}
                            >
                                Reload Tests
                            </Button>
                        </Empty>
                    ) : (
                        <>
                            {serverTestCount !== tests.length && (
                                <Alert
                                    type="warning"
                                    showIcon
                                    style={{
                                        marginBottom: 20,
                                    }}
                                    message="Test Count Verification"
                                    description={
                                        `The server reports ${serverTestCount} tests, but ${tests.length} unique tests were retrieved. Refresh the page or check the browser console if the difference persists.`
                                    }
                                />
                            )}

                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                    gap: 10,
                                    marginBottom: 19,
                                }}
                            >
                                <div>
                                    <Title
                                        level={5}
                                        style={{
                                            color: BRAND.navy,
                                            margin: 0,
                                        }}
                                    >
                                        Define Your Test
                                    </Title>

                                    <Text
                                        type="secondary"
                                        style={{
                                            fontSize: 12,
                                        }}
                                    >
                                        Follow the three steps below.
                                    </Text>
                                </div>

                                <span className="ptbc-step-pill">
                                    <CheckCircleOutlined
                                        style={{
                                            color:
                                                selectedGradeClass &&
                                                selectedSubject &&
                                                selectedPeriod
                                                    ? BRAND.success
                                                    : BRAND.gold,
                                        }}
                                    />

                                    {selectedGradeClass &&
                                    selectedSubject &&
                                    selectedPeriod
                                        ? "Selection complete"
                                        : "3 selections required"}
                                </span>
                            </div>

                            <Row gutter={[16, 16]}>
                                <Col xs={24} md={8}>
                                    <SelectionField
                                        number="01"
                                        title="Grade Class"
                                        description="Choose the examination class"
                                    >
                                        <Select
                                            showSearch
                                            allowClear
                                            size="large"
                                            placeholder="Search grade classes..."
                                            style={selectStyles}
                                            value={
                                                selectedGradeClass ||
                                                undefined
                                            }
                                            optionFilterProp="label"
                                            filterOption={(
                                                input,
                                                option
                                            ) =>
                                                String(
                                                    option?.label || ""
                                                )
                                                    .toLowerCase()
                                                    .includes(
                                                        input.toLowerCase()
                                                    )
                                            }
                                            options={gradeClassOptions.map(
                                                gradeClass => ({
                                                    value: gradeClass.id,
                                                    label: gradeClass.name,
                                                })
                                            )}
                                            onChange={value => {
                                                setSelectedGradeClass(
                                                    value ?? ""
                                                );
                                                setSelectedSubject("");
                                                setSelectedPeriod("");
                                                setPreparedTest(null);
                                            }}
                                            notFoundContent="No matching grade class"
                                        />
                                    </SelectionField>
                                </Col>

                                <Col xs={24} md={8}>
                                    <SelectionField
                                        number="02"
                                        title="Subject / Course"
                                        description="Choose the test subject"
                                    >
                                        <Select
                                            showSearch
                                            allowClear
                                            size="large"
                                            placeholder={
                                                selectedGradeClass
                                                    ? "Search subjects..."
                                                    : "Select a grade class first"
                                            }
                                            style={selectStyles}
                                            value={
                                                selectedSubject ||
                                                undefined
                                            }
                                            disabled={!selectedGradeClass}
                                            optionFilterProp="label"
                                            filterOption={(
                                                input,
                                                option
                                            ) =>
                                                String(
                                                    option?.label || ""
                                                )
                                                    .toLowerCase()
                                                    .includes(
                                                        input.toLowerCase()
                                                    )
                                            }
                                            options={subjectOptions.map(
                                                subject => ({
                                                    value: subject.id,
                                                    label: subject.name,
                                                })
                                            )}
                                            onChange={value => {
                                                setSelectedSubject(
                                                    value ?? ""
                                                );
                                                setSelectedPeriod("");
                                                setPreparedTest(null);
                                            }}
                                            notFoundContent="No subjects available for this class"
                                        />
                                    </SelectionField>
                                </Col>

                                <Col xs={24} md={8}>
                                    <SelectionField
                                        number="03"
                                        title="Period"
                                        description="Choose the examination period"
                                    >
                                        <Select
                                            showSearch
                                            allowClear
                                            size="large"
                                            placeholder={
                                                selectedSubject
                                                    ? "Search periods..."
                                                    : "Select a subject first"
                                            }
                                            style={selectStyles}
                                            value={
                                                selectedPeriod ||
                                                undefined
                                            }
                                            disabled={!selectedSubject}
                                            optionFilterProp="label"
                                            filterOption={(
                                                input,
                                                option
                                            ) =>
                                                String(
                                                    option?.label || ""
                                                )
                                                    .toLowerCase()
                                                    .includes(
                                                        input.toLowerCase()
                                                    )
                                            }
                                            options={periodOptions.map(
                                                period => ({
                                                    value: period.id,
                                                    label: period.name,
                                                })
                                            )}
                                            onChange={value => {
                                                setSelectedPeriod(
                                                    value ?? ""
                                                );
                                                setPreparedTest(null);
                                            }}
                                            notFoundContent="No periods available for this subject"
                                        />
                                    </SelectionField>
                                </Col>
                            </Row>

                            {/* Selection summary */}

                            {selectedGradeClass &&
                                selectedSubject &&
                                selectedPeriod && (
                                    <div
                                        style={{
                                            marginTop: 21,
                                            padding: 17,
                                            borderRadius: 13,
                                            background: "#F1F5FA",
                                            border: `1px solid ${BRAND.border}`,
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 9,
                                                marginBottom: 12,
                                            }}
                                        >
                                            <CheckCircleOutlined
                                                style={{
                                                    color: BRAND.success,
                                                    fontSize: 17,
                                                }}
                                            />

                                            <Text
                                                strong
                                                style={{
                                                    color: BRAND.navy,
                                                }}
                                            >
                                                Selected Examination
                                            </Text>
                                        </div>

                                        <Row gutter={[15, 12]}>
                                            <Col xs={24} sm={8}>
                                                <Text
                                                    type="secondary"
                                                    style={{
                                                        display: "block",
                                                        fontSize: 11,
                                                        marginBottom: 4,
                                                    }}
                                                >
                                                    GRADE CLASS
                                                </Text>

                                                <Text
                                                    strong
                                                    style={{
                                                        color: BRAND.navy,
                                                    }}
                                                >
                                                    {selectedGradeClassName}
                                                </Text>
                                            </Col>

                                            <Col xs={24} sm={8}>
                                                <Text
                                                    type="secondary"
                                                    style={{
                                                        display: "block",
                                                        fontSize: 11,
                                                        marginBottom: 4,
                                                    }}
                                                >
                                                    SUBJECT / COURSE
                                                </Text>

                                                <Text
                                                    strong
                                                    style={{
                                                        color: BRAND.navy,
                                                    }}
                                                >
                                                    {selectedSubjectName}
                                                </Text>
                                            </Col>

                                            <Col xs={24} sm={8}>
                                                <Text
                                                    type="secondary"
                                                    style={{
                                                        display: "block",
                                                        fontSize: 11,
                                                        marginBottom: 4,
                                                    }}
                                                >
                                                    PERIOD
                                                </Text>

                                                <Text
                                                    strong
                                                    style={{
                                                        color: BRAND.navy,
                                                    }}
                                                >
                                                    {selectedPeriodName}
                                                </Text>
                                            </Col>
                                        </Row>
                                    </div>
                                )}

                            <Divider />

                            {/* =================================
                                PREPARATION / DOWNLOAD PANEL
                            ================================= */}

                            {isReady ? (
                                <div className="ptbc-ready-panel">
                                    <Row
                                        gutter={[16, 16]}
                                        align="middle"
                                    >
                                        <Col xs={24} md={15}>
                                            <Space
                                                align="start"
                                                size={13}
                                            >
                                                <div
                                                    style={{
                                                        width: 43,
                                                        height: 43,
                                                        minWidth: 43,
                                                        borderRadius: 12,
                                                        background: "#DDF3E7",
                                                        color: BRAND.success,
                                                        display: "flex",
                                                        justifyContent: "center",
                                                        alignItems: "center",
                                                        fontSize: 21,
                                                    }}
                                                >
                                                    <CheckCircleOutlined />
                                                </div>

                                                <div>
                                                    <Title
                                                        level={5}
                                                        style={{
                                                            margin: "0 0 5px",
                                                            color: BRAND.navy,
                                                        }}
                                                    >
                                                        Test Papers Ready
                                                    </Title>

                                                    <Text
                                                        type="secondary"
                                                        style={{
                                                            fontSize: 12,
                                                            lineHeight: 1.8,
                                                        }}
                                                    >
                                                        Your examination PDF
                                                        has been prepared
                                                        for all enrolled
                                                        students in this
                                                        grade class.
                                                    </Text>

                                                    <div
                                                        style={{
                                                            marginTop: 9,
                                                        }}
                                                    >
                                                        <Tag
                                                            icon={<TeamOutlined />}
                                                            color="success"
                                                            style={{
                                                                borderRadius: 7,
                                                                marginInlineEnd: 0,
                                                            }}
                                                        >
                                                            {preparedTest.students.length}
                                                            {" "}
                                                            Student Papers
                                                        </Tag>
                                                    </div>
                                                </div>
                                            </Space>
                                        </Col>

                                        <Col
                                            xs={24}
                                            md={9}
                                            style={{
                                                textAlign: "right",
                                            }}
                                        >
                                            <PDFDownloadLink
                                                document={
                                                    <TestPDF
                                                        test={preparedTest.test}
                                                        students={preparedTest.students}
                                                        studentTests={
                                                            preparedTest.studentTests
                                                        }
                                                    />
                                                }
                                                fileName={pdfFileName}
                                                style={{
                                                    textDecoration: "none",
                                                    display: "inline-block",
                                                    width: "100%",
                                                    maxWidth: 280,
                                                }}
                                            >
                                                {({ loading }) => (
                                                    <Button
                                                        className="ptbc-download-btn"
                                                        size="large"
                                                        block
                                                        icon={
                                                            loading
                                                                ? <Spin size="small" />
                                                                : <FilePdfOutlined />
                                                        }
                                                        loading={loading}
                                                    >
                                                        {loading
                                                            ? "Generating PDF..."
                                                            : "Download Test PDF"}
                                                    </Button>
                                                )}
                                            </PDFDownloadLink>

                                            <Text
                                                type="secondary"
                                                style={{
                                                    display: "block",
                                                    fontSize: 11,
                                                    marginTop: 8,
                                                }}
                                            >
                                                PDF examination document
                                            </Text>
                                        </Col>
                                    </Row>

                                    <Divider
                                        style={{
                                            borderColor: "#D8EADF",
                                            margin: "17px 0",
                                        }}
                                    />

                                    <Button
                                        className="ptbc-secondary-btn"
                                        icon={<ReloadOutlined />}
                                        onClick={() => setPreparedTest(null)}
                                    >
                                        Prepare Again
                                    </Button>
                                </div>
                            ) : (
                                <div
                                    style={{
                                        padding: "22px 20px",
                                        borderRadius: 15,
                                        background: "#FAFBFD",
                                        border: `1px dashed ${
                                            selectedGradeClass &&
                                            selectedSubject &&
                                            selectedPeriod
                                                ? BRAND.gold
                                                : "#D9E2EC"
                                        }`,
                                    }}
                                >
                                    <Row
                                        gutter={[16, 16]}
                                        align="middle"
                                    >
                                        <Col xs={24} md={15}>
                                            <Space
                                                align="start"
                                                size={13}
                                            >
                                                <div
                                                    style={{
                                                        width: 44,
                                                        height: 44,
                                                        minWidth: 44,
                                                        borderRadius: 13,
                                                        background: BRAND.paleGold,
                                                        border: "1px solid #F0E1B8",
                                                        color: BRAND.navy,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        fontSize: 21,
                                                    }}
                                                >
                                                    <FilePdfOutlined />
                                                </div>

                                                <div>
                                                    <Text
                                                        strong
                                                        style={{
                                                            display: "block",
                                                            color: BRAND.navy,
                                                            fontSize: 14,
                                                        }}
                                                    >
                                                        Prepare Student Test Papers
                                                    </Text>

                                                    <Text
                                                        type="secondary"
                                                        style={{
                                                            display: "block",
                                                            marginTop: 5,
                                                            fontSize: 12,
                                                            lineHeight: 1.8,
                                                        }}
                                                    >
                                                        {selectedGradeClass &&
                                                        selectedSubject &&
                                                        selectedPeriod
                                                            ? "Your selection is complete. Prepare the examination document for all enrolled students in the selected class."
                                                            : "Complete all three selections above to enable examination preparation."}
                                                    </Text>
                                                </div>
                                            </Space>
                                        </Col>

                                        <Col
                                            xs={24}
                                            md={9}
                                            style={{
                                                textAlign: "right",
                                            }}
                                        >
                                            <Button
                                                className="ptbc-primary-btn"
                                                size="large"
                                                icon={
                                                    printing
                                                        ? <Spin size="small" />
                                                        : <PrinterOutlined />
                                                }
                                                loading={printing}
                                                disabled={disabled}
                                                onClick={generateTestPDF}
                                                block
                                            >
                                                {printing
                                                    ? "Preparing Papers..."
                                                    : "Prepare Test Papers"}
                                            </Button>
                                        </Col>
                                    </Row>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </Card>

            {/* =========================================
                FOOTER NOTE
            ========================================= */}

            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                    textAlign: "center",
                    marginTop: 19,
                    padding: "12px 10px",
                    color: BRAND.muted,
                    fontSize: 11,
                }}
            >
                <SafetyCertificateOutlined
                    style={{
                        color: BRAND.gold,
                    }}
                />

                <span>
                    Examination records are prepared using the
                    selected academic year and currently enrolled
                    students.
                </span>
            </div>
        </div>
    );
};

export default PrintTestByClass;






// import React, {
// useEffect,
// useMemo,
// useState,
// } from "react";

// import api from "../../api/api";
// import Swal from "sweetalert2";

// import {
// Card,
// Row,
// Col,
// Typography,
// Select,
// Button,
// Space,
// Statistic,
// Divider,
// Alert,
// Spin,
// } from "antd";

// import {
// PrinterOutlined,
// BookOutlined,
// ReadOutlined,
// CalendarOutlined,
// FilePdfOutlined,
// } from "@ant-design/icons";

// import {
// PDFDownloadLink,
// } from "@react-pdf/renderer";

// import TestPDF from "./TestPDF";

// import { useAcademicYear } from "../../AcademicYearContext";

// const extractList = data =>
// Array.isArray(data)
// ? data
// : Array.isArray(data?.results)
// ? data.results
// : [];

// const PrintTestByClass = () => {
// const [
// tests,
// setTests,
// ] = useState([]);

// const [
// loadingTests,
// setLoadingTests,
// ] = useState(true);

// const [
// selectedGradeClass,
// setSelectedGradeClass,
// ] = useState("");

// const [
// selectedSubject,
// setSelectedSubject,
// ] = useState("");

// const [
// selectedPeriod,
// setSelectedPeriod,
// ] = useState("");

// const [
// printing,
// setPrinting,
// ] = useState(false);

// const [
// preparedTest,
// setPreparedTest,
// ] = useState(null);

// const {
// selectedYear,
// loading: loadingAcademicYear,
// } = useAcademicYear();

// const token =
// localStorage.getItem(
// "access"
// );

// const authHeaders = {
// Authorization: `Bearer ${token}`,
// };

// /*

// * =====================================================
// * FETCH TESTS
// * =====================================================
//   */

// useEffect(() => {
// let mounted = true;


// const fetchTests = async () => {
//   if (loadingAcademicYear) {
//     return;
//   }

//   if (!selectedYear?.id) {
//     if (mounted) {
//       setTests([]);
//       setLoadingTests(false);
//     }

//     return;
//   }

//   if (mounted) {
//     setLoadingTests(true);
//   }

//   try {
// const response = await api.get("/api/periodic-tests/", {
//     headers: authHeaders,
//     params: {
//         academic_year: selectedYear.id,
//     },
// });

// console.log("API RESPONSE:", response.data);

// const testList = extractList(response.data);

// console.log("TEST LIST:", testList);

// if (mounted) {
//     setTests(testList);
// }
//   } catch (error) {
//     console.error(
//       "Failed to load tests:",
//       error
//     );

//     if (mounted) {
//       setTests([]);

//       Swal.fire({
//         icon: "error",
//         title: "Failed to Load Tests",
//         text: "The tests could not be loaded.",
//         confirmButtonColor: "#800000",
//       });
//     }
//   } finally {
//     if (mounted) {
//       setLoadingTests(false);
//     }
//   }
// };

// fetchTests();

// return () => {
//   mounted = false;
// };


// }, [
// selectedYear?.id,
// loadingAcademicYear,
// ]);

// /*

// * =====================================================
// * GRADE CLASS OPTIONS
// * =====================================================
//   */

// const gradeClassOptions =
// useMemo(() => {
// const map = new Map();


//   for (const test of tests) {
//     const gradeClassId =
//     test.grade_class
//       // test.grade_class_id ??
//       // test.grade_class;

//     const gradeClassName =
//       test.grade_class_name ??
//       `Grade Class ${gradeClassId}`;

//     if (!gradeClassId) {
//       continue;
//     }

//     if (map.has(gradeClassId)) {
//       continue;
//     }

//     map.set(
//       gradeClassId,
//       {
//         id: gradeClassId,
//         name: gradeClassName,
//       }
//     );
//   }

//   return [
//     ...map.values(),
//   ].sort(
//     (a, b) =>
//       a.name.localeCompare(
//         b.name
//       )
//   );
// }, [tests]);


// /*

// * =====================================================
// * SUBJECT OPTIONS
// * =====================================================
//   */

// const subjectOptions =
// useMemo(() => {
// if (!selectedGradeClass) {
// return [];
// }


//   const map = new Map();

//   for (const test of tests) {
//     const gradeClassId =
//     test.grade_class
//       // test.grade_class_id ??
//       // test.grade_class;

//     if (
//       String(
//         gradeClassId
//       ) !==
//       String(
//         selectedGradeClass
//       )
//     ) {
//       continue;
//     }

//     const subjectId =
//     test.subject
//       // test.subject_id ??
//       // test.subject;

//     const subjectName =
//       test.subject_name ??
//       `Subject ${subjectId}`;

//     if (!subjectId) {
//       continue;
//     }

//     if (map.has(subjectId)) {
//       continue;
//     }

//     map.set(
//       subjectId,
//       {
//         id: subjectId,
//         name: subjectName,
//       }
//     );
//   }

//   return [
//     ...map.values(),
//   ].sort(
//     (a, b) =>
//       a.name.localeCompare(
//         b.name
//       )
//   );
// }, [
//   tests,
//   selectedGradeClass,
// ]);


// /*

// * =====================================================
// * PERIOD OPTIONS
// * =====================================================
//   */

// const periodOptions =
// useMemo(() => {
// if (
// !selectedGradeClass ||
// !selectedSubject
// ) {
// return [];
// }


//   const map = new Map();

//   for (const test of tests) {
//     const gradeClassId =
//     test.grade_class
//       // test.grade_class_id ??
//       // test.grade_class;

//     const subjectId =
//     test.subject
//       // test.subject_id ??
//       // test.subject;

//     if (
//       String(
//         gradeClassId
//       ) !==
//         String(
//           selectedGradeClass
//         ) ||
//       String(
//         subjectId
//       ) !==
//         String(
//           selectedSubject
//         )
//     ) {
//       continue;
//     }

//     const periodId =
//     test.period
//       // test.period_id ??
//       // test.period;

//     const periodName =
//       test.period_name ??
//       `Period ${periodId}`;

//     if (!periodId) {
//       continue;
//     }

//     if (map.has(periodId)) {
//       continue;
//     }

//     map.set(
//       periodId,
//       {
//         id: periodId,
//         name: periodName,
//       }
//     );
//   }

//   return [
//     ...map.values(),
//   ].sort(
//     (a, b) =>
//       a.name.localeCompare(
//         b.name
//       )
//   );
// }, [
//   tests,
//   selectedGradeClass,
//   selectedSubject,
// ]);


// /*

// * =====================================================
// * FIND SELECTED TEST
// * =====================================================
//   */

// const findMatchingTestSummary =
// () => {
// return tests.find(test => {
// const gradeClassId =
// test.grade_class
// // test.grade_class_id ??
// // test.grade_class;


//     const subjectId =
//     test.subject
//       // test.subject_id ??
//       // test.subject;

//     const periodId =
//     test.period
//       // test.period_id ??
//       // test.period;

//     return (
//       String(
//         gradeClassId
//       ) ===
//         String(
//           selectedGradeClass
//         ) &&
//       String(
//         subjectId
//       ) ===
//         String(
//           selectedSubject
//         ) &&
//       String(
//         periodId
//       ) ===
//         String(
//           selectedPeriod
//         )
//     );
//   });
// };


// /*

// * =====================================================
// * FETCH TEST DETAIL
// * =====================================================
//   */

// const fetchTestDetail =
// async testId => {
// const response =
// await api.get(
// `/api/periodic-tests/${testId}/`,
// {
// headers: authHeaders,
// }
// );


//   return response.data;
// };


// /*

// * =====================================================
// * FETCH ENROLLED STUDENTS
// * =====================================================
//   */

// const fetchEnrolledStudents =
// async gradeClassId => {
// if (!selectedYear?.id) {
// return [];
// }


//   const response =
//     await api.get(
//       "/api/users/students/",
//       {
//         headers: authHeaders,
//         params: {
//           grade_class:
//             gradeClassId,
//           status: "Enrolled",
//           academic_year:
//             selectedYear.id,
//         },
//       }
//     );

//   return extractList(
//     response.data
//   );
// };


// /*

// * =====================================================
// * PREPARE STUDENT TESTS
// * =====================================================
//   */

// const prepareStudentTests =
// (
// test,
// students
// ) => {
// return students.map(
// student => ({
// ...test,


//       student_id:
//         student.id,

//       student_name:
//         student.full_name,

//       full_name:
//         student.full_name,

//       student_admission:
//         student.admission_number,

//       student_admission_number:
//         student.admission_number,
//     })
//   );
// };


// /*

// * =====================================================
// * PREPARE TEST PDF DATA
// * =====================================================
//   */

// const generateTestPDF =
// async () => {
// if (
// !selectedGradeClass ||
// !selectedSubject ||
// !selectedPeriod
// ) {
// await Swal.fire({
// icon: "warning",
// title: "Selection Required",
// text: "Please select Grade Class, Subject, and Period.",
// confirmButtonColor: "#800000",
// });


//     return;
//   }

//   const summary =
//     findMatchingTestSummary();

//   if (!summary) {
//     await Swal.fire({
//       icon: "info",
//       title: "No Test Found",
//       text: "No test was found for the selected Grade Class, Subject, and Period.",
//       confirmButtonColor: "#800000",
//     });

//     return;
//   }

//   setPrinting(true);

//   try {
//     const test =
//       await fetchTestDetail(
//         summary.id
//       );

//     const students =
//       await fetchEnrolledStudents(
//         selectedGradeClass
//       );

//     if (!students.length) {
//       await Swal.fire({
//         icon: "info",
//         title: "No Students Found",
//         text: "No enrolled students were found in the selected Grade Class.",
//         confirmButtonColor: "#800000",
//       });

//       return;
//     }

//     const studentTests =
//       prepareStudentTests(
//         test,
//         students
//       );

//     setPreparedTest({
//       test,
//       students,
//       studentTests,
//     });

//     await Swal.fire({
//       icon: "success",
//       title: "Test Papers Prepared",
//       text: `${students.length} student test paper(s) are ready for download.`,
//       confirmButtonColor: "#800000",
//       timer: 2500,
//       showConfirmButton: false,
//     });
//   } catch (error) {
//     console.error(
//       "Failed to prepare test PDF:",
//       error
//     );

//     setPreparedTest(null);

//     await Swal.fire({
//       icon: "error",
//       title: "Preparation Failed",
//       text:
//         error?.response?.data?.detail ||
//         "The test papers could not be prepared.",
//       confirmButtonColor: "#800000",
//     });
//   } finally {
//     setPrinting(false);
//   }
// };


// /*

// * =====================================================
// * PDF FILE NAME
// * =====================================================
//   */

// const pdfFileName =
// `test_${(
//       selectedSubject ||
//       "subject"
//     ).toString()}_${
//       selectedGradeClass ||
//       "class"
//     }_${
//       selectedPeriod ||
//       "period"
//     }_students.pdf`;

// const {
// Title,
// Text,
// } = Typography;

// const disabled =
// loadingTests ||
// loadingAcademicYear ||
// printing ||
// !selectedGradeClass ||
// !selectedSubject ||
// !selectedPeriod;

// return (
// <div
// style={{
// padding: 24,
// background:
// "transparent",
// minHeight:
// "100vh",
// }}
// >
// <Card
// bordered={false}
// style={{
// borderRadius: 18,
// boxShadow:
// "0 8px 30px rgba(0,0,0,.08)",
// }}
// >
// <Space
// direction="vertical"
// size={2}
// style={{
// width: "100%",
// marginBottom: 25,
// }}
// >
// <Title
// level={3}
// style={{
// margin: 0,
// color: "#800000",
// display:
// "flex",
// alignItems:
// "center",
// gap: 10,
// }}
// > <PrinterOutlined />


//         Print Test By Class
//       </Title>

//       <Text type="secondary">
//         Generate professionally formatted examination papers for every enrolled student.
//       </Text>

//       <Text type="secondary">
//         Active Academic Year:{" "}

//         <strong>
//           {loadingAcademicYear
//             ? "Loading..."
//             : selectedYear?.name ||
//               "No Active Year"}
//         </strong>
//       </Text>
//     </Space>

//     <Row
//       gutter={16}
//       style={{
//         marginBottom: 25,
//       }}
//     >
//       <Col
//         xs={24}
//         md={8}
//       >
//         <Card
//           bordered={false}
//           style={{
//             background:
//               "#fafafa",
//           }}
//         >
//           <Statistic
//             title="Available Tests"
//             value={
//               tests.length
//             }
//             prefix={
//               <BookOutlined
//                 style={{
//                   color:
//                     "#800000",
//                 }}
//               />
//             }
//           />
//         </Card>
//       </Col>

//       <Col
//         xs={24}
//         md={8}
//       >
//         <Card
//           bordered={false}
//           style={{
//             background:
//               "#fafafa",
//           }}
//         >
//           <Statistic
//             title="Grade Classes"
//             value={
//               gradeClassOptions.length
//             }
//             prefix={
//               <ReadOutlined
//                 style={{
//                   color:
//                     "#800000",
//                 }}
//               />
//             }
//           />
//         </Card>
//       </Col>

//       <Col
//         xs={24}
//         md={8}
//       >
//         <Card
//           bordered={false}
//           style={{
//             background:
//               "#fafafa",
//           }}
//         >
//           <Statistic
//             title="Periods"
//             value={
//               periodOptions.length
//             }
//             prefix={
//               <CalendarOutlined
//                 style={{
//                   color:
//                     "#800000",
//                 }}
//               />
//             }
//           />
//         </Card>
//       </Col>
//     </Row>

//     <Divider orientation="left">
//       Select Examination
//     </Divider>

//     {!loadingAcademicYear &&
//       !loadingTests &&
//       !tests.length && (
//         <Alert
//           type="warning"
//           showIcon
//           style={{
//             marginBottom: 20,
//           }}
//           message="No Tests Available"
//           description={`No tests were found for the active academic year: ${
//             selectedYear?.name ||
//             "Unknown"
//           }.`}
//         />
//       )}

//     <Row
//       gutter={[
//         20,
//         20,
//       ]}
//     >
//       <Col
//         xs={24}
//         md={8}
//       >
//         <Text strong>
//           Grade Class
//         </Text>

//         <Select
//           showSearch
//           loading={
//             loadingTests ||
//             loadingAcademicYear
//           }
//           style={{
//             width:
//               "100%",
//             marginTop: 6,
//           }}
//           placeholder="Select Grade Class"
//           value={
//             selectedGradeClass ||
//             undefined
//           }
//           optionFilterProp="label"
//           options={gradeClassOptions.map(
//             gradeClass => ({
//               value:
//                 gradeClass.id,
//               label:
//                 gradeClass.name,
//             })
//           )}
//           onChange={value => {
//             setSelectedGradeClass(
//               value
//             );

//             setSelectedSubject(
//               ""
//             );

//             setSelectedPeriod(
//               ""
//             );

//             setPreparedTest(
//               null
//             );
//           }}
//         />
//       </Col>

//       <Col
//         xs={24}
//         md={8}
//       >
//         <Text strong>
//           Subject
//         </Text>

//         <Select
//           showSearch
//           loading={
//             loadingTests ||
//             loadingAcademicYear
//           }
//           style={{
//             width:
//               "100%",
//             marginTop: 6,
//           }}
//           placeholder="Select Subject"
//           value={
//             selectedSubject ||
//             undefined
//           }
//           disabled={
//             !selectedGradeClass
//           }
//           optionFilterProp="label"
//           options={subjectOptions.map(
//             subject => ({
//               value:
//                 subject.id,
//               label:
//                 subject.name,
//             })
//           )}
//           onChange={value => {
//             setSelectedSubject(
//               value
//             );

//             setSelectedPeriod(
//               ""
//             );

//             setPreparedTest(
//               null
//             );
//           }}
//         />
//       </Col>

//       <Col
//         xs={24}
//         md={8}
//       >
//         <Text strong>
//           Period
//         </Text>

//         <Select
//           showSearch
//           loading={
//             loadingTests ||
//             loadingAcademicYear
//           }
//           style={{
//             width:
//               "100%",
//             marginTop: 6,
//           }}
//           placeholder="Select Period"
//           value={
//             selectedPeriod ||
//             undefined
//           }
//           disabled={
//             !selectedSubject
//           }
//           optionFilterProp="label"
//           options={periodOptions.map(
//             period => ({
//               value:
//                 period.id,
//               label:
//                 period.name,
//             })
//           )}
//           onChange={value => {
//             setSelectedPeriod(
//               value
//             );

//             setPreparedTest(
//               null
//             );
//           }}
//         />
//       </Col>
//     </Row>

//     {selectedGradeClass &&
//       selectedSubject &&
//       selectedPeriod && (
//         <Alert
//           style={{
//             marginTop: 25,
//             borderRadius: 12,
//           }}
//           type="info"
//           showIcon
//           message={
//             preparedTest
//               ? "Test Papers Ready"
//               : "Ready to Prepare"
//           }
//           description={
//             preparedTest
//               ? `${preparedTest.students.length} student test paper(s) are ready. Click the download button below.`
//               : "Click Prepare Test Papers to load all enrolled students in the selected Grade Class."
//           }
//         />
//       )}

//     <div
//       style={{
//         marginTop: 35,
//         textAlign:
//           "center",
//       }}
//     >
//       {!preparedTest ? (
//         <Button
//           type="primary"
//           size="large"
//           icon={
//             printing ? (
//               <Spin
//                 size="small"
//               />
//             ) : (
//               <FilePdfOutlined />
//             )
//           }
//           loading={
//             printing
//           }
//           disabled={
//             disabled
//           }
//           onClick={
//             generateTestPDF
//           }
//           style={{
//             height: 55,
//             padding:
//               "0 45px",
//             fontSize: 17,
//             fontWeight:
//               700,
//             color:
//               "#FFFFFF",
//             background:
//               "#000000",
//             border:
//               "2px solid #FFD700",
//             borderRadius:
//               12,
//             boxShadow:
//               "0 8px 20px rgba(0,0,0,.35)",
//             transition:
//               "all 0.3s ease-in-out",
//           }}
//           onMouseEnter={event => {
//             event.currentTarget.style.background =
//               "#8B2500";

//             event.currentTarget.style.color =
//               "#FFFFFF";

//             event.currentTarget.style.borderColor =
//               "#FFD700";

//             event.currentTarget.style.boxShadow =
//               "0 10px 25px rgba(139,37,0,.5)";

//             event.currentTarget.style.transform =
//               "translateY(-2px)";
//           }}
//           onMouseLeave={event => {
//             event.currentTarget.style.background =
//               "#000000";

//             event.currentTarget.style.color =
//               "#FFFFFF";

//             event.currentTarget.style.borderColor =
//               "#FFD700";

//             event.currentTarget.style.boxShadow =
//               "0 8px 20px rgba(0,0,0,.35)";

//             event.currentTarget.style.transform =
//               "translateY(0)";
//           }}
//         >
//           Prepare Test Papers
//         </Button>
//       ) : (
//         <PDFDownloadLink
//           document={
//             <TestPDF
//               test={
//                 preparedTest.test
//               }
//               students={
//                 preparedTest.students
//               }
//               studentTests={
//                 preparedTest.studentTests
//               }
//             />
//           }
//           fileName={
//             pdfFileName
//           }
//           style={{
//             textDecoration:
//               "none",
//           }}
//         >
//           {({
//             loading,
//           }) => (
//             <Button
//               type="primary"
//               size="large"
//               icon={
//                 loading ? (
//                   <Spin
//                     size="small"
//                   />
//                 ) : (
//                   <FilePdfOutlined />
//                 )
//               }
//               loading={
//                 loading
//               }
//               style={{
//                 height: 55,
//                 padding:
//                   "0 45px",
//                 fontSize: 17,
//                 fontWeight:
//                   700,
//                 color:
//                   "#FFFFFF",
//                 background:
//                   "linear-gradient(135deg,#800000,#b22222)",
//                 border:
//                   "none",
//                 borderRadius:
//                   12,
//                 boxShadow:
//                   "0 8px 20px rgba(128,0,0,.35)",
//               }}
//             >
//               Download Test PDF
//             </Button>
//           )}
//         </PDFDownloadLink>
//       )}
//     </div>
//   </Card>
// </div>


// );
// };

// export default PrintTestByClass;
